import React, { useEffect, useState } from 'react';
import styles from './ChromeSmartAppBanner.module.css';

const APP_STORE_ID = '6749822141';
const APP_STORE_URL = `https://apps.apple.com/app/id${APP_STORE_ID}`;
const STORAGE_KEY = 'chrome-smart-app-banner-dismissed-at';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

type AppMetadata = {
  trackName?: string;
  artistName?: string;
  artworkUrl60?: string;
  formattedPrice?: string;
};

const defaultMetadata: Required<AppMetadata> = {
  trackName: 'Drop AI',
  artistName: 'Drop AI Inc.',
  artworkUrl60: '/images/app-icon.svg',
  formattedPrice: '無料',
};

const isChromeOnIos = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent;
  return /iP(hone|od|ad)/.test(ua) && /CriOS/.test(ua);
};

const isStandaloneDisplay = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const standalone = (window.navigator as Navigator & { standalone?: boolean })
    .standalone;
  if (standalone) {
    return true;
  }

  if (window.matchMedia) {
    const queries = [
      '(display-mode: standalone)',
      '(display-mode: minimal-ui)',
      '(display-mode: fullscreen)',
    ];
    return queries.some((query) => window.matchMedia(query).matches);
  }

  return false;
};

const isLocalhost = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  const hostname = window.location.hostname;
  return ['localhost', '127.0.0.1', '::1', '0.0.0.0'].includes(hostname);
};

const RESOLVED_ENV =
  typeof process !== 'undefined' &&
  typeof process.env !== 'undefined' &&
  process.env.NODE_ENV
    ? process.env.NODE_ENV
    : 'production';

const isDevPreviewOverride = () =>
  isLocalhost();

const logDebug = (payload: Record<string, unknown>) => {
  if (typeof console === 'undefined') {
    return;
  }
  console.log('[ChromeSmartAppBanner]', {
    ...payload,
    env: RESOLVED_ENV,
  });
};

const shouldShowBanner = () => {
  if (typeof window === 'undefined') {
    logDebug({ reason: 'no-window', decision: false });
    return false;
  }

  const chromeOnIos = isChromeOnIos();
  const devOverride = isDevPreviewOverride();
  const standalone = isStandaloneDisplay();

  if ((!chromeOnIos && !devOverride) || standalone) {
    logDebug({
      reason: 'environment-check',
      chromeOnIos,
      devOverride,
      standalone,
      decision: false,
    });
    return false;
  }

  try {
    const dismissedAtRaw = localStorage.getItem(STORAGE_KEY);
    if (!dismissedAtRaw) {
      logDebug({
        reason: 'no-dismiss-record',
        chromeOnIos,
        devOverride,
        standalone,
        decision: true,
      });
      return true;
    }
    const dismissedTime = Number(dismissedAtRaw);
    if (Number.isNaN(dismissedTime)) {
      logDebug({
        reason: 'dismiss-record-invalid',
        dismissedAtRaw,
        chromeOnIos,
        devOverride,
        standalone,
        decision: true,
      });
      return true;
    }
    const dismissedExpired = Date.now() - dismissedTime > DISMISS_DURATION_MS;
    const decision = dismissedExpired;
    logDebug({
      reason: 'dismiss-record',
      dismissedAt: dismissedTime,
      dismissedExpired,
      chromeOnIos,
      devOverride,
      standalone,
      decision,
    });
    return decision;
  } catch (error) {
    logDebug({
      reason: 'storage-error',
      error: error instanceof Error ? error.message : String(error),
      chromeOnIos,
      devOverride,
      standalone,
      decision: true,
    });
    return true;
  }
};

export const ChromeSmartAppBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [metadata, setMetadata] = useState<AppMetadata>(defaultMetadata);

  useEffect(() => {
    if (shouldShowBanner()) {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    if (!visible) {
      return;
    }

    let isCancelled = false;

    const fetchMetadata = async () => {
      try {
        const response = await fetch(
          `https://itunes.apple.com/lookup?id=${APP_STORE_ID}`,
        );
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        const results = data?.results;
        if (
          Array.isArray(results) &&
          results.length > 0 &&
          !isCancelled
        ) {
          const { trackName, artistName, artworkUrl60, formattedPrice } =
            results[0] as AppMetadata;
          setMetadata((current) => ({
            ...current,
            trackName: trackName || current.trackName,
            artistName: artistName || current.artistName,
            artworkUrl60: current.artworkUrl60,
            formattedPrice: formattedPrice || current.formattedPrice,
          }));
        }
      } catch {
        // Ignore lookup failures; fallback metadata is sufficient.
      }
    };

    fetchMetadata();

    return () => {
      isCancelled = true;
    };
  }, [visible]);

  const handleDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // Swallow storage errors, banner will reappear if storage is not available.
    }
    setVisible(false);
  };

  if (!visible) {
    return null;
  }

  return (
    <div className={styles.container} role="region" aria-label="Install on iOS">
      <button
        type="button"
        className={styles.closeButton}
        aria-label="閉じる"
        onClick={handleDismiss}
      >
        ×
      </button>
      <img
        className={styles.icon}
        src={metadata.artworkUrl60 || defaultMetadata.artworkUrl60}
        alt={`${metadata.trackName || defaultMetadata.trackName} icon`}
      />
      <div className={styles.info}>
        <span className={styles.title}>
          {metadata.trackName || defaultMetadata.trackName}
        </span>
        <span className={styles.subtitle}>
          {metadata.artistName || defaultMetadata.artistName}
        </span>
        <span className={styles.copy}>App Storeで入手</span>
      </div>
      <a
        className={styles.cta}
        href={APP_STORE_URL}
        onClick={handleDismiss}
        rel="noopener noreferrer"
      >
        表示
      </a>
    </div>
  );
};

export default ChromeSmartAppBanner;
